import { useParams } from 'react-router-dom'
import { useEvent } from '../hooks/useEvents'
import { useActivities } from '../hooks/useActivities'
import { usePresentationTimetable } from '../hooks/useServices'
import { useBriefingTickets } from '../hooks/useTicketTypes'
import { useRoster } from '../hooks/useRoster'
import { formatEventDates } from '../lib/dates'
import { formatGBP } from '../lib/money'
import { PrintShell, Section, Table, Empty, usePrintOnReady } from '../components/print/printKit'
import type { PresTimetableRow } from '../types'

// EventPlanDoc — the public-facing "Event Plan" (item 14). A lighter, more
// widely-distributed companion to the internal Production Plan: what it is,
// where + when it runs, the activities, the timetable (with capacity booked /
// free by day), ticket prices, the roles on the day and access. Deliberately NO
// risks, tasks, components or financials. Route /events/:id/event-plan.

function fmtDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
  })
}

export function EventPlanDoc() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const { data: event } = useEvent(id)
  const { data: activities } = useActivities(id)
  const { data: timetable } = usePresentationTimetable(id)
  const { data: tickets } = useBriefingTickets(id)
  const { data: roster } = useRoster(id)

  const ready = !!event && activities != null && timetable != null && tickets != null && roster != null
  usePrintOnReady(ready)

  if (!event) return <PrintShell><p>Loading…</p></PrintShell>

  const when = formatEventDates(event.dates, event.startDate, event.endDate)
  const services: PresTimetableRow[] = (timetable?.services ?? [])
    .filter((s) => s.serviceType !== 'pullman' && s.serviceType !== 'dining')
  const hasDates = services.some((s) => s.date)

  // Capacity booked / free "by day from the schedule". For MERAC (dated) rows we
  // group by operating day; for a manual single timetable we show the per-day
  // figure × the operating-day count.
  const free = (cap: number | null, booked: number | null) => Math.max(0, (cap ?? 0) - (booked ?? 0))
  const byDay = new Map<string, { cap: number; booked: number }>()
  if (hasDates) {
    for (const s of services) {
      if (!s.date) continue
      const g = byDay.get(s.date) ?? { cap: 0, booked: 0 }
      g.cap += s.capacity ?? 0
      g.booked += s.booked ?? 0
      byDay.set(s.date, g)
    }
  }
  const perDayCap = services.reduce((a, s) => a + (s.capacity ?? 0), 0)
  const perDayBooked = services.reduce((a, s) => a + (s.booked ?? 0), 0)
  const operatingDays = event.dates?.length && event.dates.length > 0 ? event.dates.length : 1
  const anyCapacity = services.some((s) => (s.capacity ?? 0) > 0)
  const gb = (n: number) => n.toLocaleString('en-GB')

  return (
    <PrintShell>
      <section style={{ paddingBottom: 24, pageBreakAfter: 'always' }}>
        <div style={{ fontSize: 12, color: '#5a5a5a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>
          NYMR · Event plan
        </div>
        <h1 style={{ fontSize: 36 }}>{event.name}</h1>
        <p style={{ fontSize: 14, color: '#5a5a5a' }}>{event.type.name} · {when}</p>
        {event.location && <p style={{ fontSize: 14 }}>{event.location}</p>}
        {event.description && <p style={{ marginTop: 16 }}>{event.description}</p>}
        <p style={{ fontSize: 11, color: '#5a5a5a', marginTop: 40 }}>Generated {new Date().toLocaleString('en-GB')}.</p>
      </section>

      <Section title="What's on">
        <p><strong>When:</strong> {when}</p>
        <p><strong>Where:</strong> {event.location ?? 'See timetable'}</p>
        {event.capacity != null && <p><strong>Expected:</strong> up to {event.capacity.toLocaleString('en-GB')} visitors</p>}
        {event.manager && <p><strong>Event lead:</strong> {event.manager.name}</p>}
      </Section>

      <Section title="Activities">
        {(activities ?? []).length === 0 ? <Empty>To be confirmed.</Empty> : (
          <Table headers={['Activity', 'Time', 'Where']}>
            {(activities ?? []).map((a) => (
              <tr key={a.id}>
                <td>
                  <strong>{a.name}</strong>
                  {a.description && <div style={{ fontSize: 10.5, color: '#5a5a5a', marginTop: 2 }}>{a.description}</div>}
                </td>
                <td>{a.timeText ?? '—'}</td>
                <td>{a.location ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Timetable">
        {services.length === 0 ? <Empty>See the day's working timetable.</Empty> : (
          <Table headers={hasDates ? ['Date', 'Time', 'Service', 'From → To', 'Capacity', 'Booked', 'Free'] : ['Time', 'Service', 'From → To', 'Capacity', 'Booked', 'Free']}>
            {services.map((s) => (
              <tr key={s.id}>
                {hasDates && <td style={{ whiteSpace: 'nowrap' }}>{s.date ? fmtDate(s.date) : '—'}</td>}
                <td style={{ fontFamily: 'monospace' }}>{s.timeText}</td>
                <td><strong>{s.serviceLabel}</strong></td>
                <td>{s.originStation && s.destinationStation ? `${s.originStation} → ${s.destinationStation}` : '—'}</td>
                <td>{s.capacity ?? '—'}</td>
                <td>{s.booked ?? '—'}</td>
                <td>{s.capacity != null ? gb(free(s.capacity, s.booked)) : '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {anyCapacity && (
        <Section title="Capacity — booked &amp; free">
          {hasDates ? (
            <Table headers={['Day', 'Capacity', 'Booked', 'Free']}>
              {Array.from(byDay.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([date, g]) => (
                <tr key={date}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(date)}</td>
                  <td>{gb(g.cap)}</td>
                  <td>{gb(g.booked)}</td>
                  <td><strong>{gb(Math.max(0, g.cap - g.booked))}</strong></td>
                </tr>
              ))}
              <tr>
                <td><strong>Total</strong></td>
                <td><strong>{gb(perDayCap)}</strong></td>
                <td><strong>{gb(perDayBooked)}</strong></td>
                <td><strong>{gb(Math.max(0, perDayCap - perDayBooked))}</strong></td>
              </tr>
            </Table>
          ) : (
            <Table headers={['Metric', 'Figure']}>
              <tr><td>Capacity per operating day</td><td>{gb(perDayCap)}</td></tr>
              <tr><td>Booked per day (current)</td><td>{gb(perDayBooked)}</td></tr>
              <tr><td>Free per day (current)</td><td><strong>{gb(Math.max(0, perDayCap - perDayBooked))}</strong></td></tr>
              <tr><td>Operating days</td><td>{gb(operatingDays)}</td></tr>
              <tr><td><strong>Total event capacity</strong></td><td><strong>{gb(perDayCap * operatingDays)}</strong></td></tr>
            </Table>
          )}
        </Section>
      )}

      <Section title="Ticket prices">
        {(tickets ?? []).length === 0 ? <Empty>Ticket prices to be confirmed.</Empty> : (
          <Table headers={['Ticket type', 'Price', 'Notes']}>
            {(tickets ?? []).map((t, i) => (
              <tr key={i}>
                <td><strong>{t.name}</strong></td>
                <td>{t.pricePence == null ? 'TBC' : formatGBP(t.pricePence)}</td>
                <td>{t.notes ?? (t.isConcession ? 'Concession' : '—')}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Roles on the day">
        {roster && roster.roles.length > 0 ? (
          <Table headers={['Role', 'Times', 'Needed / day']}>
            {roster.roles.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.name}</strong></td>
                <td>{r.defaultShiftStart && r.defaultShiftEnd ? `${r.defaultShiftStart}–${r.defaultShiftEnd}` : '—'}</td>
                <td>{r.needPerDay ?? '—'}</td>
              </tr>
            ))}
          </Table>
        ) : <Empty>Roles to be confirmed.</Empty>}
      </Section>

      <Section title="Getting here &amp; access">
        <p><strong>Stations:</strong> {event.location ?? 'Along the line — see the timetable'}</p>
        <p style={{ color: '#5a5a5a', fontSize: 11 }}>
          Car parks, entrances and exits for this event are covered in the event briefing and station
          information. Plan ahead at busy events and follow stewards' directions on the day.
        </p>
      </Section>
    </PrintShell>
  )
}
